import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { firstValueFrom } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { OrganizationService } from '../../../../services/organization/organization.service';

export interface MailSenderSelectorDialogInput
{
    selectedOptionText?: string;
}

export interface MailSenderSelectorDialogOutput
{
    selectedOption: MailSenderSelectorOption;
}

export interface MailSenderSelectorOption
{
    title: string;
    description: string;
    iconImage: string;
    route: string;
}

@UntilDestroy()
@Component({
    selector: 'app-mail-sender-selector-dialog',
    templateUrl: './mail-sender-selector-dialog.component.html',
    styleUrls: ['./mail-sender-selector-dialog.component.scss'],
})
export class MailSenderSelectorDialogComponent
{
    options: MailSenderSelectorOption[] = [];
    selectedOption: MailSenderSelectorOption = null;

    constructor(
            private _dialogRef: MatDialogRef<MailSenderSelectorDialogComponent>,
            private _organizationService: OrganizationService,
            @Inject(MAT_DIALOG_DATA) public data: MailSenderSelectorDialogInput,
    ) {
        this.assembleOptions();
    }

    /** Determine what buttons the user can see in the mail selection dialog */
    async assembleOptions(): Promise<void> {
        const org = await firstValueFrom(this._organizationService
                .getOrganizationById(null, ['preferences'])
                .pipe(untilDestroyed(this)));

        if (this.doesUserHaveModulePermission('billpay', true)) {
            this.options.push({
                title: 'Bill Pay Check',
                description: 'Mail a check to a vendor',
                iconImage: '/assets/images/app/icons/check.svg',
                route: '/app/bill-pay',
            });
        }

        if (this.doesUserHaveModulePermission('violations', true) &&
                org?.preferences?.violationsModuleEnabled) {
            this.options.push({
                title: 'Violation Reports',
                description: 'Mail violation reports',
                iconImage: '/assets/images/payhoa/bigColoredIcons/on-demand.svg',
                route: '/app/violations',
            });
        }

        if (this.doesUserHaveModulePermission('communication', true)) {
            this.options.push({
                        title: 'Custom PDF',
                        description: 'Upload and mail a PDF file',
                        iconImage: '/assets/images/app/icons/pdf.svg',
                        route: '/app/mail/send/pdf',
                    },
                    {
                        title: 'Invoices',
                        description: 'Mail invoices with optional PDF upload',
                        iconImage: '/assets/images/app/icons/invoice.svg',
                        route: '/app/mail/send/invoice',
                    });
        }

        if (this.data && this.data.selectedOptionText) {
            this.selectedOption = this.options.find(o => this.data.selectedOptionText === 'pdf' ?
                    o.title === 'Custom PDF' : o.title === 'Invoices');
        }
    }

    /** Helper to check if they have module perms */
    doesUserHaveModulePermission(
            moduleName: string,
            requiresWrite: boolean,
    ): boolean {
        return LegFiJwtService.doesUserHaveModulePermission(
                moduleName,
                requiresWrite,
        );
    }

    /** They chose an option; parent pages do different things with the selected option. */
    choose(option: MailSenderSelectorOption) {
        this._dialogRef.close({selectedOption: option});
    }

    /** Close by clicking the X; no option selected */
    close() {
        this._dialogRef.close();
    }
}
